import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, Phone, Award, Sparkles, Leaf, Lock, Zap, Star } from "lucide-react";
import {
  getCachedGoogleReviews,
  type CachedReviewsPayload,
} from "@/lib/googleReviews.functions";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import heroImg from "@/assets/Apex_Theater.webp.asset.json";
import heroSmall from "@/assets/hero-theater-640.webp";
import heroMedium from "@/assets/hero-theater-960.webp";
import smartImg from "@/assets/smart-home.webp";
import outdoorImg from "@/assets/outdoor-av-card-900.webp";
const theaterImg = heroImg.url;
import audioImg from "@/assets/whole-home-audio-card-900.webp";
import lightingImg from "@/assets/lighting-showcase-optimized.webp";
import shadingImg from "@/assets/shading-showcase-optimized.webp";
import securityCardImg from "@/assets/security-card-800.webp.asset.json";
import smartCardImg from "@/assets/smart-home-card-optimized.webp";
import brandsLifestyleImg from "@/assets/brands-lifestyle-1200.webp";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { PRIMARY_LOCAL_BUSINESS } from "@/lib/localBusinessSchema";
import { homeFaqs } from "@/data/homeFaqs";
import { faqPageSchema } from "@/data/serviceFaqs";

const HomeCarousel = lazy(() =>
  import("@/components/HomeCarousel").then((m) => ({ default: m.HomeCarousel }))
);
const BrandStrip = lazy(() =>
  import("@/components/BrandStrip").then((m) => ({ default: m.BrandStrip }))
);

const GoogleReviewsSection = lazy(() =>
  import("@/components/GoogleReviewsSection").then((m) => ({ default: m.GoogleReviewsSection }))
);

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Denver Home Theater & Home Automation Installation | Apex AV" },
      {
        name: "description",
        content:
          "Home theater, smart home and whole-home audio installation in Denver Metro. Apex AV offers a free in-home consultation, system design and a line-item proposal.",
      },
      {
        name: "keywords",
        content:
          "home automation Denver, AV installer Denver, smart home installation Denver, home theater installer Denver, whole-home audio Denver, smart home dealer Denver, custom AV company Denver, architectural lighting Denver, motorized shades Denver, TV mounting Denver, outdoor TV installation Denver, low voltage contractor Denver, custom theater room Denver, Denver Metro AV company, Front Range smart home, home theater design Denver, surround sound installation Denver, home audio installation Denver, smart lighting control Denver, security camera installation Denver, network installation Denver, Wi-Fi installation Denver, home prewire Denver, cable fishing Denver, luxury smart home Denver, residential AV integrator Denver",
      },
      { property: "og:title", content: "Denver Home Theater & Home Automation Installation | Apex AV" },
      {
        property: "og:description",
        content:
          "Home theater, smart home and whole-home audio installation in Denver Metro. Apex AV offers a free in-home consultation, system design and a line-item proposal.",
      },
      { property: "og:url", content: "https://apexav.com/" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Denver Home Theater & Home Automation Installation | Apex AV" },
      { name: "twitter:description", content: "Home theater, smart home and whole-home audio installation in Denver Metro. Free in-home consultation, system design and a line-item proposal from Apex AV." },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
    ],
    links: [
      { rel: "canonical", href: "https://apexav.com/" },
      {
        rel: "preload",
        as: "image",
        href: heroImg.url,
        imageSrcSet: `${heroSmall} 640w, ${heroMedium} 960w, ${heroImg.url} 984w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              ...PRIMARY_LOCAL_BUSINESS,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "AV & Smart Home Services",
                itemListElement: [
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Custom Home Theater Installation", url: "https://apexav.com/services/home-theater" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Smart Home Automation", url: "https://apexav.com/services/smart-home" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Whole-Home Audio Installation", url: "https://apexav.com/services/whole-home-audio" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Lighting Control", url: "https://apexav.com/services/lighting" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Motorized Shades", url: "https://apexav.com/services/shading" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Security & Surveillance", url: "https://apexav.com/services/security" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Outdoor Entertainment", url: "https://apexav.com/services/outdoor" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "TV Mounting", url: "https://apexav.com/services/tv-mounting" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Low-Voltage Prewire", url: "https://apexav.com/services/prewire" } },
                ],
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://apexav.com/#website",
              url: "https://apexav.com/",
              name: "Apex AV",
              description:
                "Denver home automation and AV installation — custom theaters, smart home, audio, lighting, shading, networking, and prewire.",
              inLanguage: "en-US",
              publisher: { "@id": "https://apexav.com/#organization" },
            },
            {
              ...faqPageSchema(homeFaqs, "https://apexav.com/"),
              "@id": "https://apexav.com/#faq",
              inLanguage: "en-US",
              about: { "@id": "https://apexav.com/#organization" },
            },
          ],
        }),

      },
    ],
  }),
  component: Home_,
});

const trustBadges = [
  "Factory Certified",
  "Industry Accredited",
  "Licensed & Insured",
  "10+ Years in Denver",
];

const experiences = [
  {
    icon: Sparkles,
    eyebrow: "Experiences",
    title: "Made for the moments that matter.",
    desc: "Reference theaters, whole-home audio, and outdoor entertainment that turn ordinary nights into something memorable.",
    to: "/services/home-theater" as const,
    image: theaterImg,
    alt: "Custom home theater entertainment experience",
  },
  {
    icon: Leaf,
    eyebrow: "Wellness",
    title: "A home that follows the sun.",
    desc: "Circadian lighting, motorized shades, and climate scenes that wake you gently and wind you down.",
    to: "/services/lighting" as const,
    image: lightingImg,
    alt: "Architectural lighting and motorized shades for wellbeing",
  },
  {
    icon: Zap,
    eyebrow: "Efficiency",
    title: "One app. Every system. Less friction.",
    desc: "Unified control of lighting, climate, audio, and security on a single, enterprise-grade network.",
    to: "/services/smart-home" as const,
    image: smartCardImg,
    alt: "Smart home unified control interface",
  },
  {
    icon: Lock,
    eyebrow: "Protection",
    title: "Peace of mind, by design.",
    desc: "4K surveillance, smart locks, alarm integration, and resilient networking that quietly watch over what matters.",
    to: "/services/security" as const,
    image: securityCardImg.url,
    alt: "Security cameras and smart locks for home protection",
  },
];

function HeroReviewBadge() {
  const load = useServerFn(getCachedGoogleReviews);
  const [data, setData] = useState<CachedReviewsPayload | null>(null);

  useEffect(() => {
    load()
      .then(setData)
      .catch(() => setData(null));
  }, [load]);

  if (!data || !data.rating || !data.user_rating_count) return null;
  const rating = data.rating;

  const featured = data.reviews.find((r) => r.rating === 5 && r.text?.length > 10);
  const quote = featured?.text ?? "";

  return (
    <div className="mt-6 inline-flex flex-col gap-2 rounded-lg border border-border bg-card/90 p-4 shadow-card backdrop-blur-sm">
      <a
        href={data.place_url ?? "#reviews"}
        target="_blank"
        rel="noreferrer"
        className="group flex flex-wrap items-center gap-2 text-sm font-medium"
      >
        <span className="flex items-center gap-0.5 text-brand-text">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${i < Math.round(rating) ? "fill-current" : "opacity-30"}`}
            />
          ))}
        </span>
        <span className="font-semibold text-foreground">{data.rating.toFixed(1)}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{data.user_rating_count} Google reviews</span>
      </a>
      {quote && (
        <p className="max-w-md text-xs text-muted-foreground italic">
          “{quote.length > 120 ? quote.slice(0, 120) + "..." : quote}”
        </p>
      )}
    </div>
  );
}

function Home_() {
  return (
    <>
      {/* Hero — full-width */}
      <section className="architectural-grid relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img
            src={heroImg.url}
            srcSet={`${heroSmall} 640w, ${heroMedium} 960w, ${heroImg.url} 984w`}
            sizes="100vw"
            alt="Apex AV custom home theater installation in Denver, Colorado — serving Cherry Creek, Highlands Ranch, Castle Rock, and Castle Pines"
            className="h-full w-full object-cover object-center opacity-70 motion-safe:animate-[cinema-drift_18s_ease-in-out_infinite]"
            width={984}
            height={656}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />

          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30" />
        </div>
        <div className="relative container mx-auto px-6 pt-6 md:pt-14">
          <div className="max-w-4xl animate-reveal-up pb-6 md:pb-12">
            <span className="eyebrow inline-flex items-center gap-4 text-brand-text">
              <span className="h-px w-10 bg-brand/70" />
              Apex AV · Denver Metro &amp; Front Range
            </span>
            <h1 className="mt-5 max-w-4xl font-display text-3xl font-medium leading-[1.1] md:text-5xl lg:text-6xl">
              Denver home theaters &amp; <span className="text-brand-text">home automation.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base text-foreground/90 md:text-lg">
              Apex AV designs, installs, and programs home theaters, whole-home audio, lighting, shades, and smart home systems for new builds, remodels, and existing homes.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild size="lg" className="h-auto min-h-12 whitespace-normal px-6 py-3 text-center">
                <Link to="/contact">Book a Free Consultation <ArrowRight /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-auto min-h-12 px-6 py-3">
                <a href="tel:+13033173233"><Phone /> (303) 317-3233</a>
              </Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">No obligation. Walk the space, discuss scope and budget, then receive a line-item proposal.</p>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
              Serving Denver Metro &amp; the Front Range, including Cherry Creek, Highlands Ranch, Castle Rock, and Boulder.{' '}
              <Link to="/service-areas" className="text-brand-text underline underline-offset-4">Check your service area</Link>
            </p>
            <HeroReviewBadge />
          </div>
          <div className="grid grid-cols-2 gap-px border-t border-border bg-border md:grid-cols-4">
              {trustBadges.map((b) => (
                <div
                  key={b}
                  className="flex min-h-14 items-center gap-3 bg-background/80 px-4 py-3 text-xs font-medium text-muted-foreground backdrop-blur-sm md:px-6"
                >
                  <Award className="h-4 w-4 shrink-0 text-brand-text" />
                  {b}
                </div>
              ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-border bg-background py-10 md:py-14" aria-labelledby="home-services-heading">
        <div className="container mx-auto px-6">
          <h2 id="home-services-heading" className="font-display text-2xl font-semibold md:text-3xl">What are you planning for your Denver home?</h2>
          <div className="mt-7 grid gap-7 md:grid-cols-3">
            <div>
              <Link to="/services/home-theater" className="inline-flex items-center gap-2 font-display text-xl font-semibold text-brand-text">Home theater installation <ArrowRight className="h-4 w-4" /></Link>
              <p className="mt-2 text-sm text-muted-foreground">Room design, projection, surround sound, and calibration for your space.</p>
            </div>
            <div>
              <Link to="/services/smart-home" className="inline-flex items-center gap-2 font-display text-xl font-semibold text-brand-text">Smart home automation <ArrowRight className="h-4 w-4" /></Link>
              <p className="mt-2 text-sm text-muted-foreground">Bring lighting, shades, climate, audio, and security into one control system.</p>
              <Link to="/services/control4" className="mt-2 inline-block text-sm text-brand-text underline underline-offset-4">Control4 installation &amp; programming</Link>
            </div>
            <div>
              <Link to="/services/whole-home-audio" className="inline-flex items-center gap-2 font-display text-xl font-semibold text-brand-text">Whole-home audio <ArrowRight className="h-4 w-4" /></Link>
              <p className="mt-2 text-sm text-muted-foreground">Music in multiple rooms with discreet speakers and simple daily controls.</p>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 border-t border-border pt-5 text-sm">
            <Link to="/services/lighting" className="text-brand-text underline underline-offset-4">Lighting control</Link>
            <Link to="/services/shading" className="text-brand-text underline underline-offset-4">Motorized shades</Link>
            <Link to="/gallery" className="text-brand-text underline underline-offset-4">View installation gallery</Link>
            <Link to="/services" className="inline-flex items-center gap-2 font-semibold">All AV services <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Featured work carousel — full-width band */}
      <section className="w-full bg-muted/50 border-y border-border">
        <Suspense fallback={<div className="min-h-[480px]" />}>
          <HomeCarousel />
        </Suspense>
      </section>

      {/* Experience categories — full-width white band */}
      <section className="w-full bg-background py-24 md:py-32">
        <div className="container mx-auto px-6">
          <Reveal className="max-w-2xl">
            <span className="eyebrow text-brand-text rule-brand">Four pillars</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold">Designed around how you actually live.</h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Every Apex AV install ladders up to one of four outcomes — the moments you want to create, the comfort you want to feel, the friction you want to remove, the people you want to protect.
            </p>
          </Reveal>
          <div className="mobile-snap-row mt-14 grid gap-6 md:grid-cols-2">
            {experiences.map((s, i) => (
              <Reveal key={s.title} delay={i * 90} className="mobile-snap-card flex">
                <Link
                  to={s.to}
                    className="card-elegant touch-ripple group relative min-h-96 w-full overflow-hidden border border-border bg-card shadow-card hover:border-brand/40 hover:shadow-glow flex flex-col"
                >
                  <img
                    src={s.image}
                    alt={s.alt}
                    loading="lazy"
                    width={1280}
                    height={896}
                    className="absolute inset-0 h-full w-full object-cover transition duration-[1200ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/35" />
                  <div className="relative z-[2] flex flex-1 flex-col p-8">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-lg bg-gradient-accent flex items-center justify-center transition duration-500 group-hover:shadow-glow group-hover:-rotate-6">
                        <s.icon className="h-5 w-5 text-accent-foreground transition-transform duration-500 group-hover:scale-110" />
                      </div>
                      <span className="eyebrow text-brand-text">{s.eyebrow}</span>
                    </div>
                    <h3 className="mt-auto pt-16 font-display text-2xl md:text-3xl font-semibold">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md">{s.desc}</p>
                    <span className="magnetic-link mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-text group-hover:gap-2 transition-all">
                      Explore {s.eyebrow.toLowerCase()} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:gap-3 transition-all"
            >
              See all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>


      {/* How we work — full-width white band */}
      <section className="w-full bg-background py-24 md:py-32 border-t border-border">
        <div className="container mx-auto px-6">
          <Reveal className="max-w-3xl">
            <span className="eyebrow text-brand-text rule-brand">How we work</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold">From first call to final walkthrough.</h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Most Denver homeowners come to us with one specific project in mind — a theater room, a whole-home audio upgrade, or lighting and shades for a new build — and end up building toward a broader smart home over a few phases. We design every project so it can grow that way: today's install is wired and programmed to support tomorrow's addition, not replaced by it.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            <div>
              <h3 className="font-display text-xl font-semibold">1. Consultation</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                A free in-home visit where we walk the space, talk through how you actually use each room, and note existing wiring, network, and construction details. You'll leave with a rough sense of scope and budget — no pressure, no obligation.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold">2. Design & proposal</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                We put together a system design and line-item quote — equipment, labor, and any coordination needed with your builder, electrician, or contractor. New construction and remodels get a prewire plan; finished homes get a retrofit plan built around minimal wall access.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold">3. Install & support</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Our technicians install, program, and calibrate the system, then walk you through daily use. We stay reachable afterward for programming changes, troubleshooting, and future phases as your home and needs evolve.
              </p>
            </div>
          </div>
          <div className="mt-14 max-w-3xl">
            <h3 className="font-display text-2xl font-semibold">What a consultation covers</h3>
            <p className="mt-3 text-muted-foreground">
              We'll ask about the rooms you spend the most time in, what's frustrating about your current setup, and whether you're planning a remodel or new build where prewiring makes sense before drywall goes up. For existing homes, we look at attic and crawlspace access, existing network equipment, and electrical panel capacity, since those details drive what's realistic for cable routing and equipment placement. Bring photos of rooms you want addressed, a rough sense of priorities (theater vs. whole-home vs. lighting first), and any deadlines tied to a move-in date or contractor schedule — it helps us give you a straighter answer on timeline and cost during that first visit.
            </p>
          </div>
        </div>
      </section>

      {/* Showcase — full-width muted band */}
      <section className="w-full bg-muted/50 py-24 md:py-32">
        <div className="container mx-auto px-6">
          <Reveal className="max-w-2xl mb-12">
            <span className="eyebrow text-brand-text rule-brand">Portfolio</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold">Custom AV &amp; smart home installations across Denver.</h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Real projects across the Front Range — home theaters, whole-home audio, lighting, shading, and security, designed and installed by certified Denver integrators.
            </p>
          </Reveal>
          <div className="mobile-snap-row grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              { img: smartImg, alt: "Smart home control panel installation Denver", title: "One touch. Total control.", desc: "Lighting, shades, climate, audio, and security — unified in elegant interfaces.", to: "/services/smart-home" as const },
              { img: outdoorImg, alt: "Outdoor TV and patio audio install Denver", title: "Made for Colorado evenings.", desc: "Outdoor TVs and landscape audio engineered for our 300 days of sunshine.", to: "/services/outdoor" as const },
              { img: apexTheaterRichImg.url, alt: "Custom home theater installation in Denver, Cherry Creek, Highlands Ranch, and Castle Rock", title: "Cinema, at home.", desc: "Reference 4K projection and immersive surround tuned to your room.", to: "/services/home-theater" as const },
              { img: audioImg, alt: "Whole-home audio install Denver", title: "Music in every room.", desc: "Discreet in-ceiling speakers, perfectly tuned — kitchen to primary suite.", to: "/services/whole-home-audio" as const },
              { img: lightingImg, alt: "Smart lighting install Denver", title: "Light, on cue.", desc: "Architectural lighting and circadian scenes that follow your day.", to: "/services/lighting" as const },
              { img: shadingImg, alt: "Motorized shades install Denver", title: "Shades that follow the sun.", desc: "Whisper-quiet motorized shades that protect interiors and frame the view.", to: "/services/shading" as const },
              { img: securityCardImg.url, alt: "Security and surveillance install Denver", title: "Always watching over.", desc: "4K surveillance, smart locks, and alarm monitoring — managed in one app.", to: "/services/security" as const },
            ].map((card, i) => (
              <Reveal key={card.title} delay={(i % 3) * 90} className="mobile-snap-card flex">
                <Link
                  to={card.to}
                  className="card-elegant sheen touch-ripple relative w-full rounded-2xl overflow-hidden shadow-card group block hover:shadow-glow bg-card border border-border hover:border-brand/40"
                >
                  <img
                    src={card.img}
                    alt={card.alt}
                    loading="lazy"
                    width={1280}
                    height={896}
                    className="h-full w-full object-cover transition duration-[1200ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                  <div className="absolute bottom-0 z-[2] p-8">
                    <h3 className="font-display text-2xl font-semibold">{card.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md">{card.desc}</p>
                    <span className="magnetic-link mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-text group-hover:gap-2 transition-all">
                      Explore {card.title.replace(/\.$/, "")} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Install — full-width white band */}
      <section className="relative w-full overflow-hidden bg-background py-24 md:py-32">
        <div className="absolute inset-0">
          <img
            src={brandsLifestyleImg}
            alt="Luxury Denver home with integrated smart home automation and audio video brands — Greenwood Village, Lone Tree, Parker"
            loading="lazy"
            width={900}
            height={506}
            className="h-full w-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
        </div>
        <div className="relative container mx-auto px-6">
          <Reveal className="max-w-3xl mx-auto text-center">
            <span className="eyebrow text-brand-text">Brands we install</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold">
              The reference standard, by name.
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
              Authorized installer of the most trusted names in audio, video, networking, and smart home —
              chosen for performance, reliability, and decades of serviceability in Denver homes.
            </p>
            <hr className="hairline mx-auto mt-10 w-40" />
          </Reveal>
          <Reveal delay={120} className="mt-14 max-w-6xl mx-auto">
            <Suspense fallback={<div className="min-h-[200px]" />}>
              <BrandStrip variant="inline" eyebrow="" title="" />
            </Suspense>
          </Reveal>
        </div>
      </section>

      {/* Service Areas — full-width muted band */}
      <section className="w-full bg-muted/50 py-24 md:py-32 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <span className="eyebrow text-brand-text">Service areas</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold">Denver Metro &amp; Front Range.</h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
              From Cherry Creek and Castle Rock to Boulder and Parker — we design and install across the entire Denver Metro and Front Range.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-x-3 gap-y-2 text-sm tracking-wide">
              {[
                { slug: "denver", name: "Denver" },
                { slug: "aurora", name: "Aurora" },
                { slug: "lakewood", name: "Lakewood" },
                { slug: "centennial", name: "Centennial" },
                { slug: "highlands-ranch", name: "Highlands Ranch" },
                { slug: "littleton", name: "Littleton" },
                { slug: "englewood", name: "Englewood" },
                { slug: "cherry-creek", name: "Cherry Creek" },
                { slug: "parker", name: "Parker" },
                { slug: "castle-rock", name: "Castle Rock" },
                { slug: "castle-pines", name: "Castle Pines" },
                { slug: "franktown", name: "Franktown" },
                { slug: "thornton", name: "Thornton" },
                { slug: "westminster", name: "Westminster" },
                { slug: "arvada", name: "Arvada" },
                { slug: "broomfield", name: "Broomfield" },
                { slug: "boulder", name: "Boulder" },
                { slug: "golden", name: "Golden" },
              ].map((a) => (
                <Link
                  key={a.slug}
                  to="/service-areas/$slug"
                  params={{ slug: a.slug }}
                  className="rounded-full border border-border bg-card/70 px-3 py-1 text-muted-foreground transition duration-300 hover:-translate-y-0.5 hover:border-brand/50 hover:bg-card hover:text-foreground hover:shadow-glow"
                >
                  {a.name}
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/service-areas" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:gap-3 transition-all">
                View all service areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-16 max-w-4xl mx-auto">
              <span className="eyebrow text-brand-text">All services</span>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold">Browse every Denver AV &amp; smart home service.</h3>
              <div className="mt-6 flex flex-wrap justify-center gap-x-3 gap-y-2 text-sm">
                {[
                  { to: "/services/av-installation" as const, label: "AV Installation" },
                  { to: "/services/home-theater" as const, label: "Home Theater" },
                  { to: "/services/smart-home" as const, label: "Smart Home" },
                  { to: "/services/whole-home-audio" as const, label: "Whole-Home Audio" },
                  { to: "/services/hidden-audio" as const, label: "Hidden Audio" },
                  { to: "/services/whole-home-video" as const, label: "Whole-Home Video" },
                  { to: "/services/outdoor" as const, label: "Outdoor AV" },
                  { to: "/services/climate-control" as const, label: "Climate Control" },
                  { to: "/services/lighting" as const, label: "Smart Lighting" },
                  { to: "/services/shading" as const, label: "Motorized Shading" },
                  { to: "/services/security" as const, label: "Security" },
                  { to: "/services/networking" as const, label: "Networking" },
                  { to: "/services/conference" as const, label: "Conference AV" },
                  { to: "/services/commercial-av" as const, label: "Commercial AV" },
                  { to: "/services/prewire" as const, label: "New Construction Prewire" },
                  { to: "/services/retrofit" as const, label: "Retrofit Cable Fishing" },
                  { to: "/services/tv-mounting" as const, label: "TV Mounting" },
                  { to: "/services/voice-control" as const, label: "Voice Control" },
                ].map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="rounded-full border border-border bg-card/70 px-3 py-1 text-muted-foreground transition duration-300 hover:-translate-y-0.5 hover:border-brand/50 hover:bg-card hover:text-foreground hover:shadow-glow"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:gap-3 transition-all">
                  Visit the services hub <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — answer-engine friendly Q&A */}
      <section className="w-full bg-background py-24 md:py-32 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <span className="eyebrow text-brand-text">Common questions</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold">
              Denver AV &amp; smart home FAQ.
            </h2>
            <dl className="mt-10 space-y-8">
              {homeFaqs.map((f) => (
                <div key={f.q} className="border-t border-border pt-6">
                  <dt className="font-display text-xl font-semibold">{f.q}</dt>
                  <dd className="mt-3 text-muted-foreground leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Google Reviews — full-width white band */}

      <section className="w-full bg-background py-24 md:py-32 border-t border-border">
        <Suspense fallback={null}>
          <GoogleReviewsSection />
        </Suspense>
      </section>

      {/* CTA — full-width muted band */}
      <section className="w-full bg-muted/50 py-24 md:py-32">
        <div className="container mx-auto px-6">
          <Reveal>
              <div className="architectural-grid relative overflow-hidden border-y border-border bg-card/60 p-10 text-center shadow-card md:p-16">
              <h2 className="relative font-display text-3xl md:text-5xl font-semibold max-w-3xl mx-auto">Ready to upgrade your home?</h2>
              <p className="relative mt-4 text-muted-foreground max-w-xl mx-auto">
                Free <strong>in-home AV consultations</strong> across the <strong>Denver Metro Area</strong>. No pressure, no jargon — just expert guidance.
              </p>
              <Link
                to="/contact"
                className="interactive-lift magnetic-link relative z-[2] mt-8 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow hover:opacity-95"
              >
                Schedule Your Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
